
import { useState } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { useToast } from '@/hooks/use-toast';
import { useSupabaseOperation } from '@/hooks/use-supabase';
import { Family } from '../types';
import { logger } from '@/utils/logger';

export const useCreateFamily = (
  userId: string | undefined,
  onSuccess: () => void
) => {
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const { executeOperation } = useSupabaseOperation();

  const createFamily = async (familyName: string) => {
    if (!familyName.trim() || !userId) {
      toast({
        title: 'Missing Information',
        description: 'Please enter a family name',
        variant: 'destructive'
      });
      return;
    }
    
    setIsCreating(true);
    
    try {
      logger.log('Attempting to create family with name:', familyName.trim());
      
      // Use executeOperation to properly handle the RPC call
      const { data: familyData, error: rpcError } = await supabase.rpc('create_family', { 
        family_name: familyName.trim() 
      }).select();
      
      if (rpcError) {
        logger.log('RPC failed, falling back to direct insert:', rpcError);
        
        // Fallback to direct insert
        const { data: insertData, error: insertError } = await supabase
          .from('families')
          .insert({
            name: familyName.trim(),
            created_by: userId
          })
          .select()
          .single();
        
        if (insertError) {
          toast({
            title: 'Error',
            description: 'Could not create family. Please try again later.',
            variant: 'destructive'
          });
          setIsCreating(false);
          return;
        }
        
        // Add current user as owner of the family
        const { error: memberError } = await supabase
          .from('family_members')
          .insert({
            family_id: insertData.id,
            user_id: userId,
            role: 'owner',
            status: 'active',
            joined_at: new Date().toISOString()
          });
          
        if (memberError) {
          toast({
            title: 'Error',
            description: 'Family created but could not add you as member.',
            variant: 'destructive'
          });
          setIsCreating(false);
          return;
        }
        
        toast({
          title: 'Success',
          description: 'Family created successfully!'
        });
        
        // Notify about success
        onSuccess();
      } else if (familyData) {
        toast({
          title: 'Success',
          description: 'Family created successfully!'
        });
        
        // Notify about success
        onSuccess();
      }
    } catch (error: unknown) {
      logger.error('Error in createFamily:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: 'Error',
        description: 'An unexpected error occurred: ' + message,
        variant: 'destructive'
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    isCreating,
    createFamily
  };
};
